import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { formatStatsPeriodLabel } from '../../../admin-stats-period/admin-stats-period.helpers';
import { AdminReportsService } from '../../../../services/admin-reports.service';
import {
  ReportDto,
  ReportSortField,
  ReportSortOrder,
} from '../../reports.types';

@Component({
  selector: 'admin-tool-reports-list',
  imports: [LoadingInlineComponent],
  templateUrl: './reports-list.html',
  styleUrl: './reports-list.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsList implements OnInit, OnDestroy {
  rows: ReportDto[] = [];
  total = 0;
  sortField: ReportSortField = 'startDate';
  sortOrder: ReportSortOrder = 'DESC';
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  error: string | null = null;

  private subscriptionList = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminReportsService = inject(AdminReportsService);

  get isLoading(): boolean {
    return this.loadingState === 'loading';
  }

  get hasRowData(): boolean {
    return this.rows.length > 0;
  }

  get recordCountLabel(): string {
    if (this.loadingState === 'loading' && !this.hasRowData) {
      return 'Loading…';
    }
    return `${this.total.toLocaleString()} saved report${this.total === 1 ? '' : 's'}`;
  }

  ngOnInit(): void {
    this.loadRows();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  periodLabel(row: ReportDto): string {
    return formatStatsPeriodLabel(this.asDateOnly(row.startDate), this.asDateOnly(row.endDate));
  }

  formatDate(value: string | Date | null | undefined): string {
    if (!value) {
      return '—';
    }

    try {
      return format(new Date(value), 'dd/MM/yyyy HH:mm');
    } catch {
      return '—';
    }
  }

  toggleSort(field: ReportSortField): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortField = field;
      this.sortOrder = field === 'startDate' || field === 'createdDate' ? 'DESC' : 'DESC';
    }

    this.loadRows();
  }

  isSortedBy(field: ReportSortField): boolean {
    return this.sortField === field;
  }

  private loadRows(): void {
    this.loadingState = 'loading';
    this.error = null;
    this.refreshView();

    this.subscriptionList.add(
      this.adminReportsService
        .list({
          take: 200,
          skip: 0,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
        })
        .subscribe({
          next: (response) => {
            this.rows = response.data;
            this.total = response.total;
            this.loadingState = 'loaded';
            this.refreshView();
          },
          error: () => {
            this.rows = [];
            this.total = 0;
            this.loadingState = 'error';
            this.error = 'Failed to load saved reports.';
            this.refreshView();
          },
        }),
    );
  }

  private asDateOnly(value: string | Date): string {
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return value.slice(0, 10);
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
